import { useCallback, useEffect, useState, type FC } from "react";
import { Outlet, useParams } from "react-router";
import RestaurantList from "./components/RestaurantList";
import Map from "./components/Map";
import { ArrowDownWideNarrowIcon, FunnelIcon } from "lucide-react";
import Text from "@/components/Text";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import Filter from "./components/Filter";
import {
  getRestaurantsExplorer,
  restaurantsExplorerState,
} from "@/store/restaurantsExplorerState";
import { searchBarState } from "@/store/searchBarState";
import {
  QueryFilterCondition,
  QueryFilterField,
  QuerySortField,
  type MapPin,
  type QueryFilter,
  type Restaurant,
} from "@/API";
import { filterExplorerState } from "@/store/filterExplorerState";
import {
  getRestaurantsPins,
  restaurantsPinsState,
} from "@/store/restaurantsPinsState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import useDebounce from "@/hooks/useDebounce";
import PopoverMoreActions from "./components/PopoverMoreActions";
import {
  initAddedRestaurantInFavoriteList,
  userFavoriteListState,
} from "@/store/userFavoriteListState";
import { toast } from "sonner";
import { triggerEvent } from "@/lib/utils";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import { Lock } from "lucide-react";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import DrawerRestaurantsList from "./components/DrawerRestaurantsList";

export type ViewMode = "list" | "map";

const SORT_OPTIONS = [
  {
    label: "sort_option_best_krunsh_score",
    value: QuerySortField.KRUNSH_SCORE,
  },
  {
    label: "sort_option_fastest_delivery",
    value: QuerySortField.DELIVERY_TIME,
  },
  {
    label: "sort_option_best_rating",
    value: QuerySortField.RATING,
  },
  {
    label: "sort_option_new",
    value: QuerySortField.FIRST_SEEN_AT,
  },
];

type CombinedResearch = {
  filter: QueryFilter[];
  sortOption: QuerySortField | undefined;
};

const Explorer: FC = () => {
  const { sm } = useDetectSizeDevice();
  const { restaurantId } = useParams<{ restaurantId?: string }>();
  const { t } = useTranslation();
  const { data, isLoading, isFetching } = restaurantsExplorerState();
  const { data: mapPinsResult, isLoading: pinsIsloading } =
    restaurantsPinsState();
  const { textToSearch } = searchBarState();
  const { filter } = filterExplorerState();
  const {
    restaurantJustAdded,
    restaurantsJustAdded,
    favoriteNameListJustAdded,
  } = userFavoriteListState();
  const { data: planUser } = currentSubscriptionState();

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [sortOption, setSortOption] = useState<QuerySortField | undefined>(
    undefined,
  );
  const [combinedResearch, setCombinedResearch] =
    useState<CombinedResearch | null>();
  const debouncedCombinedResearch = useDebounce(combinedResearch, 300);

  useEffect(() => {
    if (favoriteNameListJustAdded) {
      if (restaurantJustAdded) {
        toast.success(
          <div className="flex items-center gap-[12px]">
            <img
              height={40}
              width={40}
              className="object-cover"
              src={restaurantJustAdded?.imageUrl ?? undefined}
            />
            <Text fontWeight={400} fontSize={16} color="#172B4D">
              {t("favorite_restaurant_added", {
                favoriteName: favoriteNameListJustAdded,
              })}
            </Text>
          </div>,
          { duration: 2000, icon: <></>, style: { paddingLeft: 0 } },
        );
        initAddedRestaurantInFavoriteList();
      }
      if (restaurantsJustAdded) {
        toast.success(
          t("favorite_restaurants_added", {
            favoriteName: favoriteNameListJustAdded,
          }),
          {
            duration: 2000,
          },
        );
        initAddedRestaurantInFavoriteList();
      }
    }
  }, [restaurantJustAdded, restaurantsJustAdded, favoriteNameListJustAdded]);

  useEffect(() => {
    return () => {
      triggerEvent("Explorer Closed");
    };
  }, []);

  useEffect(() => {
    if (debouncedCombinedResearch) {
      getRestaurantsExplorer(
        debouncedCombinedResearch.filter,
        debouncedCombinedResearch.sortOption,
      );
      getRestaurantsPins(debouncedCombinedResearch.filter);
    }
  }, [debouncedCombinedResearch]);

  useEffect(() => {
    const filterQuery = [...filter];

    if (textToSearch)
      filterQuery.push({
        textValue: textToSearch,
        field: QueryFilterField.GENERAL_TEXT,
        condition: QueryFilterCondition.INCLUDES,
      });

    if (
      filterQuery.findIndex(
        ({ field }) => field === QueryFilterField.BOTTOM_RIGHT_GPS,
      ) !== -1 &&
      filterQuery.findIndex(
        ({ field }) => field === QueryFilterField.TOP_LEFT_GPS,
      ) !== -1
    ) {
      setCombinedResearch({
        filter: filterQuery,
        sortOption,
      });
    }
  }, [textToSearch, filter, sortOption]);

  const fetchRestaurant = useCallback(
    (append = false) => {
      const filterQuery = [...filter];

      if (textToSearch)
        filterQuery.push({
          textValue: textToSearch,
          field: QueryFilterField.GENERAL_TEXT,
          condition: QueryFilterCondition.INCLUDES,
        });

      getRestaurantsExplorer(filterQuery, sortOption, append);
    },
    [filter, textToSearch, sortOption],
  );

  useEffect(() => {
    if (data?.data.length === 0) {
      triggerEvent("Search No Results", {
        filter: JSON.stringify(filter),
        search: textToSearch,
      });
    }
  }, [data, filter, textToSearch]);

  useEffect(() => {
    if (mapPinsResult && debouncedCombinedResearch) {
      const { data, total } = mapPinsResult;
      if (data.length < total) {
        getRestaurantsPins(debouncedCombinedResearch.filter, true);
      }
    }
  }, [mapPinsResult, debouncedCombinedResearch]);

  const filterLength = filter.filter(
    ({ field }) =>
      !(
        field == QueryFilterField.BOTTOM_RIGHT_GPS ||
        field == QueryFilterField.TOP_LEFT_GPS
      ),
  ).length;

  const viewModeParents = viewMode;

  return (
    <>
      {!restaurantId &&
        (!sm ? (
          <div className="full-w bg-[#FFFFFF] px-[16px] py-[12px] flex items-center justify-between">
            <SearchBar />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondaryOutlined" className="max-w-[150px]">
                  <ArrowDownWideNarrowIcon
                    height={16}
                    width={16}
                    color="#44546F"
                  />
                  {sortOption === undefined ? (
                    <Text fontSize={14} fontWeight={600} className="truncate">
                      {t("sort")}
                    </Text>
                  ) : (
                    <Text fontSize={14} fontWeight={600} className="truncate">
                      {`${t("sort_by")} ${t(
                        SORT_OPTIONS.find(({ value }) => value === sortOption)
                          ?.label ?? "",
                      )}`}
                    </Text>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  {SORT_OPTIONS.map(({ label, value }) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => {
                        if (
                          !planUser &&
                          value === QuerySortField.FIRST_SEEN_AT
                        ) {
                          setActionTriggerNeedCore(true);
                        } else {
                          if (value !== sortOption) {
                            setSortOption(value);
                            triggerEvent("Sort Applied", {
                              sort_key: value,
                            });
                          } else {
                            setSortOption(undefined);
                          }
                        }
                      }}
                    >
                      <Button
                        variant={
                          value === sortOption
                            ? "secondaryFilled"
                            : "secondaryOutlined"
                        }
                      >
                        <Text fontSize={14} fontWeight={600}>
                          {t(label)}
                        </Text>
                        {!planUser &&
                          value === QuerySortField.FIRST_SEEN_AT && (
                            <Lock width={24} height={24} color="#535862" />
                          )}
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            <Filter filter={filter}>
              <Button
                variant={filterLength ? "secondaryFilled" : "secondaryOutlined"}
              >
                <FunnelIcon height={16} width={16} color="#44546F" />
                <Text fontSize={14} fontWeight={600}>
                  {t("filter")}
                </Text>
                {filterLength > 0 && (
                  <div className="bg-[white] h-[16px] w-[16px] flex justify-center items-center rounded-[12px]">
                    <Text fontSize={12} fontWeight={600} color="#000000">
                      {filterLength}
                    </Text>
                  </div>
                )}
              </Button>
            </Filter>
          </div>
        ) : (
          <div className="full-w bg-[#F7F8F9] px-[16px] py-[12px] flex items-center gap-[12px]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant={"secondaryOutlined"}>
                  <ArrowDownWideNarrowIcon
                    height={16}
                    width={16}
                    color="#44546F"
                  />
                  {sortOption === undefined ? (
                    <Text fontSize={14} fontWeight={600}>
                      {t("sort")}
                    </Text>
                  ) : (
                    <Text fontSize={14} fontWeight={600}>
                      {`${t("sort_by")} ${t(
                        SORT_OPTIONS.find(({ value }) => value === sortOption)
                          ?.label ?? "",
                      )}`}
                    </Text>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuGroup>
                  {SORT_OPTIONS.map(({ label, value }) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => {
                        if (
                          !planUser &&
                          value === QuerySortField.FIRST_SEEN_AT
                        ) {
                          setActionTriggerNeedCore(true);
                        } else {
                          if (value !== sortOption) {
                            setSortOption(value);
                            triggerEvent("Sort Applied", {
                              sort_key: value,
                            });
                          } else {
                            setSortOption(undefined);
                          }
                        }
                      }}
                    >
                      <Button
                        variant={
                          value === sortOption
                            ? "secondaryFilled"
                            : "secondaryOutlined"
                        }
                      >
                        <Text fontSize={14} fontWeight={600}>
                          {t(label)}
                        </Text>
                        {!planUser &&
                          value === QuerySortField.FIRST_SEEN_AT && (
                            <Lock width={24} height={24} color="#535862" />
                          )}
                      </Button>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Filter filter={filter}>
              <Button
                variant={filterLength ? "secondaryFilled" : "secondaryOutlined"}
              >
                <FunnelIcon height={16} width={16} color="#44546F" />
                <Text fontSize={14} fontWeight={600}>
                  {t("filter")}
                </Text>
                {filterLength > 0 && (
                  <div className="bg-[white] h-[16px] w-[16px] flex justify-center items-center rounded-[12px]">
                    <Text fontSize={12} fontWeight={600} color="#000000">
                      {filterLength}
                    </Text>
                  </div>
                )}
              </Button>
            </Filter>
            {mapPinsResult?.data &&
              mapPinsResult?.data.length > 1 &&
              mapPinsResult?.data.length < 401 && (
                <PopoverMoreActions
                  restaurantsId={
                    mapPinsResult.data
                      .filter((restaurant) => restaurant?.id)
                      .map(
                        (restaurant) => restaurant && restaurant.id,
                      ) as string[]
                  }
                />
              )}
          </div>
        ))}

      <div className="flex relative">
        <Outlet context={[viewModeParents]} />
        {!restaurantId && sm && (
          <RestaurantList
            // restaurantsError={restaurantsError}
            fetchRestaurant={fetchRestaurant}
            isFetching={isFetching}
            viewMode={viewMode}
            isLoading={isLoading}
            totalRestaurant={data?.total ?? 0}
            restaurants={
              data?.data.filter((restaurant) => restaurant) as
                | Restaurant[]
                | undefined
                | null
            }
            pins={(mapPinsResult?.data as MapPin[] | null) ?? null}
          />
        )}
        {!restaurantId && !sm && (
          <DrawerRestaurantsList
            fetchRestaurant={fetchRestaurant}
            isFetching={isFetching}
            viewMode={viewMode}
            isLoading={isLoading}
            data={data}
            sortOption={sortOption}
            setSortOption={setSortOption}
            totalRestaurant={data?.total ?? 0}
            restaurants={
              data?.data.filter((restaurant) => restaurant) as
                | Restaurant[]
                | undefined
                | null
            }
            setViewMode={setViewMode}
            pins={(mapPinsResult?.data as MapPin[] | null) ?? null}
            pinsIsloading={pinsIsloading}
          />
        )}

        {sm && (
          <Map
            setViewMode={setViewMode}
            viewMode={viewMode}
            restaurants={(mapPinsResult?.data as MapPin[] | null) ?? null}
            pinsIsloading={pinsIsloading}
          />
        )}

        {/* Mobile float ink */}
        {/* {pathname === "/explorer" && (
          <div
            onClick={() => setViewMode(viewMode === "map" ? "list" : "map")}
            style={{ transform: "translate(-50%)" }}
            className=" z-[2] cursor-pointer fixed px-[16px] gap-[8px] py-[8px] bg-[#414651] bottom-[60px] left-[50%] rounded-[12px] items-center justify-center flex sm:hidden"
          >
            <Text fontSize={14} fontWeight={600} color="#E9EAEB">
              {viewMode === "map" ? "Liste" : "Carte"}
            </Text>
            {viewMode === "map" ? (
              <List height={24} width={24} color="white" />
            ) : (
              <MapIcon height={24} width={24} color="white" />
            )}
          </div>
        )} */}
      </div>
    </>
  );
};

export default Explorer;
